import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Loader2, Check } from "lucide-react";
import { useT } from "@/lib/i18n";
import LangSwitcher from "@/components/LangSwitcher";
import RatchetLogo from "@/components/RatchetLogo";
import OTPInput from "@/components/OTPInput";
import TotpEnroll from "@/components/TotpEnroll";

export default function Signup() {
  const t = useT();
  const { requestSignup, verifyEmailOtp, resendEmailOtp } = useAuth();
  const nav = useNavigate();

  const [stage, setStage] = useState("form");   // form → otp → totp
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "" });
  const [code, setCode] = useState("");
  const [preauth, setPreauth] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const submitForm = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await requestSignup(form);
      setStage("otp"); setCooldown(30);
    } catch (e) {
      setErr(e?.response?.data?.detail || t("auth.otp.err.generic"));
    } finally { setLoading(false); }
  };

  const verify = async (value) => {
    const c = value ?? code;
    setErr(""); setLoading(true);
    try {
      const data = await verifyEmailOtp(form.email, c);
      setPreauth(data.preauth_token);
      setStage("totp");
    } catch (e) {
      setErr(e?.response?.data?.detail || t("auth.otp.err.invalid"));
      setCode("");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setErr("");
    try { await resendEmailOtp(form.email); setCooldown(30); }
    catch (e) { setErr(e?.response?.data?.detail || t("auth.otp.err.generic")); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between p-12 w-1/2 border-r border-white/5 bg-gradient-to-br from-black via-[#050A1A] to-black">
        <Link to="/" className="flex items-center gap-2">
          <RatchetLogo size={28} />
          <span className="font-display font-black tracking-tight">RATCHET</span>
        </Link>
        <div className="space-y-3">
          <div className="overline mb-3">{t("auth.signup.includes")}</div>
          {[t("auth.signup.b1"), t("auth.signup.b2"), t("auth.signup.b3"), t("auth.signup.b4")].map((b) => (
            <div key={b} className="flex items-center gap-3 text-white/80 text-sm">
              <Check className="w-4 h-4 text-primary" />
              <span>{b}</span>
            </div>
          ))}
        </div>
        <div className="overline">{t("auth.signup.noCard")}</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6"><LangSwitcher /></div>

        {stage === "form" && (
          <form onSubmit={submitForm} className="w-full max-w-sm space-y-4" data-testid="signup-form">
            <div>
              <div className="overline mb-2">{t("auth.signup.startFree")}</div>
              <h1 className="font-display font-black text-3xl tracking-tighter">{t("auth.signup.title")}</h1>
            </div>
            <div>
              <label className="overline block mb-1.5">{t("auth.signup.yourName")}</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:border-primary focus:outline-none"
                data-testid="signup-name-input" />
            </div>
            <div>
              <label className="overline block mb-1.5">{t("auth.signup.org")}</label>
              <input required value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:border-primary focus:outline-none"
                placeholder="Acme Inc" data-testid="signup-org-input" />
            </div>
            <div>
              <label className="overline block mb-1.5">{t("auth.signup.workEmail")}</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:border-primary focus:outline-none font-mono"
                data-testid="signup-email-input" />
            </div>
            <div>
              <label className="overline block mb-1.5">{t("auth.signup.pwd")}</label>
              <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:border-primary focus:outline-none font-mono"
                data-testid="signup-password-input" />
            </div>
            {err && <div className="text-sm text-destructive font-mono" data-testid="signup-error">{err}</div>}
            <button disabled={loading} className="btn-primary w-full" data-testid="signup-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.signup.create")}
            </button>
            <p className="text-sm text-white/40 text-center">
              {t("auth.alreadyCustomer")} <Link to="/login" className="text-primary hover:underline" data-testid="signup-link-login">{t("nav.signin")}</Link>
            </p>
          </form>
        )}

        {stage === "otp" && (
          <div className="w-full max-w-sm space-y-5" data-testid="signup-otp">
            <div>
              <div className="overline mb-2">{t("auth.signup.startFree")}</div>
              <h1 className="font-display font-black text-3xl tracking-tighter">{t("auth.otp.title")}</h1>
              <p className="text-sm text-white/50 mt-2">{t("auth.otp.subtitle").replace("{email}", form.email)}</p>
            </div>
            <OTPInput value={code} onChange={setCode} onComplete={verify} disabled={loading} error={!!err} />
            {err && <div className="text-sm text-destructive font-mono text-center" data-testid="signup-otp-error">{err}</div>}
            <button disabled={loading || code.length < 6} onClick={() => verify()} className="btn-primary w-full" data-testid="signup-otp-verify">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.otp.verify")}
            </button>
            <div className="flex items-center justify-between text-sm">
              <button onClick={resend} disabled={cooldown > 0} className="text-primary disabled:text-white/30 hover:underline disabled:no-underline" data-testid="signup-otp-resend">
                {cooldown > 0 ? t("auth.otp.resendIn").replace("{n}", cooldown) : t("auth.otp.resend")}
              </button>
              <button onClick={() => { setStage("form"); setCode(""); setErr(""); }} className="text-white/40 hover:text-white/70" data-testid="signup-otp-back">
                {t("auth.otp.useDifferent")}
              </button>
            </div>
          </div>
        )}

        {stage === "totp" && (
          <TotpEnroll preauth={preauth} onDone={() => nav("/onboarding")} />
        )}
      </div>
    </div>
  );
}
